import { Switch, Route, useLocation, Redirect, Router as WouterRouter } from "wouter";
import { Suspense, useEffect } from "react";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { trackPhoneTap } from "./lib/analytics";
import { useAnalytics } from "./hooks/use-analytics";
import {
  dynamicRoutes,
  notFound,
  redirectRoutes,
  runtimeRoutes,
} from "./clientRouteManifest";
import type { ComponentType } from "react";

type AppRoute = { path: string; component: ComponentType };
type RedirectRoute = { from: string; to: string };

function ScrollToTop() {
  const [location] = useLocation();

  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash) {
      requestAnimationFrame(() => {
        document.getElementById(hash)?.scrollIntoView({ block: "start" });
      });
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return null;
}

function AppRoutes({
  forceNotFound = false,
  routes = runtimeRoutes,
  parameterizedRoutes = dynamicRoutes,
  redirects = redirectRoutes,
  notFoundComponent = notFound,
}: {
  forceNotFound?: boolean;
  routes?: readonly AppRoute[];
  parameterizedRoutes?: readonly AppRoute[];
  redirects?: readonly RedirectRoute[];
  notFoundComponent?: ComponentType;
}) {
  useAnalytics();
  const NotFound = notFoundComponent;

  return (
    <>
      <ScrollToTop />
      {forceNotFound ? <NotFound /> : (
      <Switch>
        {routes.map(({ path, component }) => (
          <Route key={path} path={path}>
            {() => {
              const Component = component;
              return <Suspense fallback={null}><Component /></Suspense>;
            }}
          </Route>
        ))}
        {parameterizedRoutes.map(({ path, component }) => (
          <Route key={path} path={path}>
            {() => {
              const Component = component;
              return <Suspense fallback={null}><Component /></Suspense>;
            }}
          </Route>
        ))}
        {redirects.map(({ from, to }) => (
          <Route key={from} path={from}>{() => <Redirect to={to} />}</Route>
        ))}
        <Route>{() => <Suspense fallback={null}><NotFound /></Suspense>}</Route>
      </Switch>
      )}
    </>
  );
}

export default function App({
  ssrPath,
  forceNotFound = false,
  routes,
  parameterizedRoutes,
  redirects,
  notFoundComponent,
}: {
  ssrPath?: string;
  forceNotFound?: boolean;
  routes?: readonly AppRoute[];
  parameterizedRoutes?: readonly AppRoute[];
  redirects?: readonly RedirectRoute[];
  notFoundComponent?: ComponentType;
}) {
  useEffect(() => {
    const trackTelephoneLinks = (event: MouseEvent) => {
      const target = event.target as Element | null;
      if (target?.closest('a[href^="tel:"]')) trackPhoneTap();
    };
    document.addEventListener("click", trackTelephoneLinks);
    return () => document.removeEventListener("click", trackTelephoneLinks);
  }, []);

  const content = (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <AppRoutes
          forceNotFound={forceNotFound}
          routes={routes}
          parameterizedRoutes={parameterizedRoutes}
          redirects={redirects}
          notFoundComponent={notFoundComponent}
        />
      </TooltipProvider>
    </QueryClientProvider>
  );

  return ssrPath ? <WouterRouter ssrPath={ssrPath}>{content}</WouterRouter> : content;
}